import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Drawer, FormControl,
  IconButton, InputAdornment, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import MarkChatReadOutlinedIcon from '@mui/icons-material/MarkChatReadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { canAccessEmergency } from '../data/turkeyProvinces'
import { loadEmergencyRecipients, subscribeEmergencyHistory } from '../services/emergencyCalls'
import './EmergencyReportPage.css'

const dateText = (value) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : null)
  return date ? new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Istanbul' }).format(date) : '—'
}
const normalize = (value) => String(value || '').toLocaleLowerCase('tr-TR')
const delivery = (row) => {
  const status = String(row.deliveryStatus || '').toUpperCase()
  const send = String(row.sendStatus || '').toLowerCase()
  if (status === 'READ') return { label: 'Okundu', tone: 'green', group: 'delivered' }
  if (status === 'DELIVERED') return { label: 'Teslim edildi', tone: 'green', group: 'delivered' }
  if (['FAILED', 'UNDELIVERED', 'CANCELLED', 'EXPIRED'].includes(status)) return { label: 'Teslim edilemedi', tone: 'red', group: 'failed' }
  if (status === 'SENT') return { label: 'Gönderildi', tone: 'blue', group: 'sent' }
  if (send === 'accepted') return { label: 'API kabul etti', tone: 'blue', group: 'sent' }
  if (['api_rejected', 'invalid_phone', 'duplicate_phone', 'not_started_deadline'].includes(send)) {
    const labels = { api_rejected: 'API reddetti', invalid_phone: 'Geçersiz telefon', duplicate_phone: 'Tekrarlı telefon', not_started_deadline: 'Süre doldu' }
    return { label: labels[send], tone: 'red', group: 'failed' }
  }
  if (send === 'uncertain') return { label: 'Sonuç belirsiz', tone: 'amber', group: 'pending' }
  return { label: send === 'requesting' ? 'Gönderiliyor' : 'Gönderim bekliyor', tone: 'amber', group: 'pending' }
}
const csvCell = (value) => {
  const text = String(value ?? '')
  const safe = /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text
  return `"${safe.replace(/"/g, '""')}"`
}

export default function EmergencyReportPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { currentUser, userStatus } = useAuth()
  const hasAccess = canAccessEmergency(currentUser, userStatus)
  const [calls, setCalls] = useState([])
  const [callsLoading, setCallsLoading] = useState(true)
  const [callsError, setCallsError] = useState('')
  const [recipients, setRecipients] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [city, setCity] = useState('all')
  const [status, setStatus] = useState('all')
  const [detail, setDetail] = useState(null)
  const selectedId = params.get('call') || ''
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const selectedCall = calls.find((call) => call.id === selectedId)

  useEffect(() => {
    if (!hasAccess) return undefined
    return subscribeEmergencyHistory((items) => {
      setCalls(items)
      setCallsLoading(false)
      setCallsError('')
    }, () => {
      setCallsLoading(false)
      setCallsError('Çağrı geçmişi yüklenemedi. Erişim izinlerini ve bağlantıyı kontrol edin.')
    })
  }, [hasAccess])

  useEffect(() => {
    if (!selectedId && calls.length) setParams({ call: calls[0].id }, { replace: true })
  }, [selectedId, calls, setParams])

  useEffect(() => {
    setRecipients([])
    setCursor(null)
    setError('')
    setSearch('')
    setCity('all')
    setStatus('all')
    setDetail(null)
    if (!hasAccess || !selectedId || !selectedCall) return undefined
    let active = true
    setLoading(true)
    loadEmergencyRecipients(selectedId).then((page) => {
      if (!active) return
      setRecipients(page.items)
      setCursor(page.cursor)
      setLoading(false)
    }).catch(() => {
      if (!active) return
      setError('Alıcı kayıtları yüklenemedi. Firestore okuma iznini ve bağlantıyı kontrol edin.')
      setLoading(false)
    })
    return () => { active = false }
  }, [hasAccess, selectedId, Boolean(selectedCall)]) // eslint-disable-line react-hooks/exhaustive-deps

  const cities = useMemo(() => [...new Set(recipients.map((item) => item.province).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr')), [recipients])
  const filtered = useMemo(() => recipients.filter((item) => {
    if (city !== 'all' && item.province !== city) return false
    if (status !== 'all' && delivery(item).group !== status) return false
    const needle = normalize(search.trim())
    return !needle || [item.userName, item.phone, item.personId, item.refvalue, item.province].some((value) => normalize(value).includes(needle))
  }), [recipients, city, status, search])
  const counts = useMemo(() => recipients.reduce((result, item) => {
    result[delivery(item).group] += 1
    return result
  }, { delivered: 0, sent: 0, pending: 0, failed: 0 }), [recipients])

  async function loadMore() {
    if (!cursor || loading) return
    setLoading(true)
    setError('')
    try {
      const page = await loadEmergencyRecipients(selectedId, cursor)
      if (selectedIdRef.current !== selectedId) return
      setRecipients((current) => [...current, ...page.items])
      setCursor(page.cursor)
    } catch {
      if (selectedIdRef.current === selectedId) setError('Sonraki kayıtlar yüklenemedi. Tekrar deneyin.')
    } finally {
      if (selectedIdRef.current === selectedId) setLoading(false)
    }
  }

  function downloadCsv() {
    const columns = ['Ad soyad', 'İl', 'Telefon', 'Gönderim durumu', 'Teslimat durumu', 'Oluşturulma', 'Kabul', 'Teslim', 'Okunma', 'Kişi ID', 'Kayıt ID']
    const lines = [columns, ...filtered.map((row) => [row.userName, row.province, row.phone, row.sendStatus, row.deliveryStatus, dateText(row.createdAt), dateText(row.acceptedAt), dateText(row.deliveredAt), dateText(row.readAt), row.personId, row.id])]
    const blob = new Blob(['\ufeff', lines.map((line) => line.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `acil-durum-raporu-${selectedId}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  if (!hasAccess) return <Container sx={{ py: 4 }}><Alert severity="error">Bu rapora yalnızca aktif acil durum ekibi üyeleri erişebilir.</Alert></Container>

  return <Box component="main" className="emergency-report-page"><Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
    <Box className="er-heading">
      <Box>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/emergency')} sx={{ mb: 1, textTransform: 'none' }}>Acil durum ekranına dön</Button>
        <Typography component="h1" className="er-title"><AssessmentOutlinedIcon /> Gönderim raporu</Typography>
        <Typography color="text.secondary">Çağrıların kişi bazındaki gönderim, teslimat ve okunma durumlarını inceleyin.</Typography>
      </Box>
      <Box className="er-live"><span /> Firestore kayıtları</Box>
    </Box>

    {callsError && <Alert severity="error" sx={{ mb: 2 }}>{callsError}</Alert>}
    <Box className="er-shell">
      <Box component="aside" className="er-calls">
        <Box className="er-section-head"><Typography component="h2" fontWeight={800}>Çağrılar</Typography><span>Son 100</span></Box>
        {callsLoading ? <Box className="er-center"><CircularProgress size={24} /></Box> : !calls.length ? <Box className="er-empty">Henüz çağrı kaydı yok.</Box> : calls.map((call) => <button type="button" key={call.id} className={`er-call ${selectedId === call.id ? 'active' : ''}`} onClick={() => setParams({ call: call.id })}>
          <span className="er-call-top"><strong>{call.scope === 'nationwide' ? 'Türkiye geneli' : (call.provinces || []).map((p) => p.name).join(', ') || 'İl seçimi'}</strong><span className={`er-dot ${call.status || 'unknown'}`} /></span>
          <span className="er-call-meta">{dateText(call.createdAt)}</span>
          <span className="er-call-meta">{call.initiatedByName || call.initiatedByEmail || 'Bilinmeyen kullanıcı'}</span>
        </button>)}
      </Box>

      <Box className="er-main">
        {!selectedCall ? <Box className="er-empty">{callsLoading ? 'Çağrılar yükleniyor…' : 'Raporlamak için bir çağrı seçin.'}</Box> : <>
          <Box className="er-overview">
            <Box><span className="er-eyebrow">SEÇİLİ ÇAĞRI</span><Typography component="h2" fontWeight={850} sx={{ fontSize: { xs: 21, md: 26 }, mt: .6 }}>{selectedCall.scope === 'nationwide' ? 'Türkiye geneli' : (selectedCall.provinces || []).map((p) => p.name).join(', ')}</Typography><Typography color="text.secondary" variant="body2" sx={{ mt: .5 }}>{dateText(selectedCall.createdAt)} · {selectedCall.initiatedByName || selectedCall.initiatedByEmail || 'Bilinmeyen kullanıcı'}</Typography></Box>
            <Chip size="small" label={{ accepted: 'API isteği kabul edildi', pending: 'Çağrı bekliyor', rejected: 'Çağrı reddedildi', unknown: 'Çağrı sonucu belirsiz' }[selectedCall.status] || 'Durum bilinmiyor'} color={selectedCall.status === 'accepted' ? 'success' : selectedCall.status === 'rejected' ? 'error' : 'warning'} variant="outlined" />
          </Box>
          <Box className="er-id">Çağrı kimliği: <code>{selectedCall.id}</code></Box>
          {selectedCall.apiResponseText && <Alert severity="info" sx={{ mb: 2 }}>{selectedCall.apiResponseText}</Alert>}
          <Box className="er-stats">
            <div className="er-stat"><PeopleOutlineIcon /><span>Listelenen alıcı</span><strong>{recipients.length}{cursor ? '+' : ''}</strong></div>
            <div className="er-stat green"><MarkChatReadOutlinedIcon /><span>Teslim / okundu</span><strong>{counts.delivered}</strong></div>
            <div className="er-stat blue"><AssessmentOutlinedIcon /><span>Gönderildi / kabul</span><strong>{counts.sent}</strong></div>
            <div className="er-stat amber"><RefreshIcon /><span>Bekliyor / belirsiz</span><strong>{counts.pending}</strong></div>
            <div className="er-stat red"><ErrorOutlineIcon /><span>Başarısız / atlandı</span><strong>{counts.failed}</strong></div>
          </Box>
          {selectedCall.apiResult?.matchedUsers != null && <Typography variant="body2" className="er-total-note">API eşleşen kişi sayısı: <strong>{selectedCall.apiResult.matchedUsers}</strong>. Yukarıdaki durum sayıları yalnızca yüklenen kayıtlara aittir.</Typography>}
          {cursor && <Typography variant="body2" className="er-total-note">Daha fazla kayıt var. Tüm alıcıları görmek için listenin altındaki “Daha fazla yükle” düğmesini kullanın.</Typography>}
          <Box className="er-toolbar">
            <TextField size="small" label="Ad, telefon, kişi ID ara" value={search} onChange={(event) => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <FormControl size="small"><Select aria-label="İl filtresi" value={city} onChange={(event) => setCity(event.target.value)}><MenuItem value="all">Tüm iller</MenuItem>{cities.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</Select></FormControl>
            <FormControl size="small"><Select aria-label="Durum filtresi" value={status} onChange={(event) => setStatus(event.target.value)}><MenuItem value="all">Tüm durumlar</MenuItem><MenuItem value="delivered">Teslim / okundu</MenuItem><MenuItem value="sent">Gönderildi / kabul</MenuItem><MenuItem value="pending">Bekliyor / belirsiz</MenuItem><MenuItem value="failed">Başarısız / atlandı</MenuItem></Select></FormControl>
            <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={downloadCsv} disabled={!filtered.length} sx={{ textTransform: 'none' }}>CSV indir</Button>
          </Box>
          <Box className="er-list-head"><Typography component="h3" fontWeight={800}>Alıcılar <span>({filtered.length})</span></Typography><Typography variant="caption">Satıra tıklayarak tüm ayrıntıları görün</Typography></Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && !recipients.length ? <Box className="er-center"><CircularProgress size={26} /></Box> : !filtered.length ? <Box className="er-empty">{recipients.length ? 'Filtrelerle eşleşen alıcı yok.' : 'Bu çağrı için alıcı kaydı bulunamadı.'}</Box> : <Box className="er-table-wrap"><table className="er-table"><thead><tr><th>Alıcı</th><th>İl</th><th>Telefon</th><th>Durum</th><th>Son işlem</th></tr></thead><tbody>{filtered.map((row) => <tr key={row.id} onClick={() => setDetail(row)} tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter') setDetail(row) }}><td><strong>{row.userName || 'İsim yok'}</strong><small>{row.refvalue || row.personId || '—'}</small></td><td>{row.province || '—'}</td><td>{row.phone || '—'}</td><td><span className={`er-status ${delivery(row).tone}`}>{delivery(row).label}</span></td><td>{dateText(row.lastStatusAt || row.updatedAt || row.createdAt)}</td></tr>)}</tbody></table></Box>}
          {cursor && <Box className="er-more"><Button variant="outlined" onClick={loadMore} disabled={loading}>{loading ? 'Yükleniyor…' : 'Daha fazla yükle'}</Button></Box>}
          <Typography className="er-footnote">API kabulü, mesajın teslim edildiği anlamına gelmez. Teslim ve okunma durumları webhook kayıtlarından gelir. CSV yalnızca yüklenen ve filtrelenen satırları içerir.</Typography>
        </>}
      </Box>
    </Box>
    <Drawer anchor="right" open={Boolean(detail)} onClose={() => setDetail(null)} PaperProps={{ className: 'er-drawer' }}><Box className="er-drawer-head"><Box><span className="er-eyebrow">ALICI AYRINTISI</span><Typography component="h2" variant="h5" fontWeight={800}>{detail?.userName || 'İsim yok'}</Typography></Box><Tooltip title="Kapat"><IconButton onClick={() => setDetail(null)}><CloseIcon /></IconButton></Tooltip></Box>{detail && <Box className="er-drawer-body"><span className={`er-status ${delivery(detail).tone}`}>{delivery(detail).label}</span><Typography component="h3" fontWeight={800} sx={{ mt: 3, mb: 1 }}>Kişi ve gönderim</Typography>{[['İl', detail.province], ['Telefon', detail.phone], ['Telefon kaynağı', detail.phoneSource], ['Rol', detail.role], ['Referans', detail.refvalue], ['Kişi ID', detail.personId], ['Gönderim durumu', detail.sendStatus], ['Teslimat durumu', detail.deliveryStatus], ['Deneme sayısı', detail.attemptCount], ['API durum kodu', detail.apiStatusCode], ['API hatası', detail.apiError], ['Teslimat hatası', typeof detail.deliveryError === 'object' ? JSON.stringify(detail.deliveryError) : detail.deliveryError], ['EDNA mesaj ID', detail.ednaMessageId], ['EDNA çıkış ID', detail.ednaOutMessageId]].map(([label, value]) => <div className="er-detail-line" key={label}><span>{label}</span><strong>{value ?? '—'}</strong></div>)}<Typography component="h3" fontWeight={800} sx={{ mt: 3, mb: 1 }}>Zaman çizelgesi</Typography>{[['Kayıt', detail.createdAt], ['API isteği', detail.requestedAt], ['API kabulü', detail.acceptedAt], ['Son durum', detail.lastStatusAt], ['Teslim', detail.deliveredAt], ['Okunma', detail.readAt], ['Son gelen yanıt', detail.lastInboundAt]].map(([label, value]) => <div className="er-detail-line" key={label}><span>{label}</span><strong>{dateText(value)}</strong></div>)}<Typography component="h3" fontWeight={800} sx={{ mt: 3, mb: 1 }}>Kayıt bilgisi</Typography><div className="er-detail-line"><span>Kayıt ID</span><strong>{detail.id}</strong></div><div className="er-detail-line"><span>Yanıt sayısı</span><strong>{detail.replyCount ?? 0}</strong></div></Box>}</Drawer>
  </Container></Box>
}
